import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, HeartHandshake, Sparkles, Phone, MapPin,
  Home as HomeIcon, Accessibility, Stethoscope, Users,
  CheckCircle2, Zap, ClipboardCheck,
} from "lucide-react";
import { api } from "../lib/api";
import { useAccessibility } from "../lib/accessibility";
import { useSEO, organizationSchema, localBusinessSchema, breadcrumbSchema } from "../lib/seo";
import VacancyCard from "../components/VacancyCard";
import PostcodeLookup from "../components/PostcodeLookup";
import VideoStories from "../components/VideoStories";
import FeaturedHome from "../components/FeaturedHome";
import HeroSlider from "../components/HeroSlider";
import TrustStrip from "../components/TrustStrip";
import AnimatedAusMap from "../components/AnimatedAusMap";
import DayInTheLife from "../components/DayInTheLife";
import AudienceCards from "../components/AudienceCards";
import { trackPhoneClick } from "../lib/tracking";

// Visual service tiles — image + one-line label (conversion-focused, low text).
const SERVICE_TILES = [
  {
    slug: "sil",
    label: "Supported Independent Living",
    img: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: HomeIcon,
  },
  {
    slug: "sda",
    label: "Specialist Disability Accommodation",
    img: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: Accessibility,
  },
  {
    slug: "community-nursing",
    label: "Community Nursing Care",
    img: "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: Stethoscope,
  },
  {
    slug: "support-coordination",
    label: "Support Coordination",
    img: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: HeartHandshake,
  },
  {
    slug: "community-participation",
    label: "Community Participation",
    img: "https://images.unsplash.com/photo-1511632765486-a01980e01a18?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: Users,
  },
  {
    slug: "daily-living",
    label: "Assistance with Daily Living",
    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    Icon: Sparkles,
  },
];

const STAT_STRIP = [
  ["24/7", "Active SIL & on-call"],
  ["4", "SDA categories delivered"],
  ["1 day", "Typical intake response"],
  ["100%", "NDIS-registered services"],
];

const OUTCOMES = [
  { Icon: Zap, title: "Fast intake", text: "Response within one business day." },
  { Icon: ShieldCheck, title: "Registered & audited", text: "Practice-Standard aligned." },
  { Icon: HeartHandshake, title: "Named coordinator", text: "One person who knows your plan." },
];

const TRUST_PILLS = [
  "NDIS Registered",
  "Practice Standards",
  "Worker Screened",
  "Trauma-informed",
  "PBS-aligned",
  "LGBTQIA+ friendly",
  "Culturally-safe",
  "24/7 on-call",
];

export default function HomePage() {
  const { simple } = useAccessibility();
  const [featured, setFeatured] = useState([]);

  useSEO({
    title: "Alpha Community Care | Registered NDIS Provider · SIL & SDA Melbourne, Victoria & Perth",
    description:
      "Premium NDIS support across Melbourne, Victoria and Perth — SIL, SDA, community nursing, daily living and support coordination. Registered NDIS provider with real homes, 1:1 and 1:2 support only, national intake.",
    keywords: [
      "NDIS provider Melbourne",
      "NDIS provider Victoria",
      "NDIS provider Australia",
      "registered NDIS provider",
      "SIL Melbourne",
      "SDA Melbourne",
      "SIL Victoria",
      "SDA Victoria",
      "SIL vacancies Melbourne",
      "NDIS housing Melbourne",
      "Supported Independent Living Melbourne",
      "Specialist Disability Accommodation Melbourne",
      "Community Nursing Melbourne",
      "Support Coordination Melbourne",
      "NDIS Perth",
      "SIL Perth",
      "SDA Perth",
      "disability support provider Melbourne",
    ],
    canonical: "https://alphacommunitycare.com.au/",
    jsonLd: [organizationSchema(), localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }])],
  });

  useEffect(() => {
    api.get("/vacancies?limit=3").then((r) => setFeatured(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* ==================== CINEMATIC HERO SLIDER ==================== */}
      <HeroSlider />

      {/* Overlapping trust strip — bridges hero to next section */}
      <TrustStrip />

      {/* ==================== STAT STRIP (editorial · restrained) ==================== */}
      <section className="bg-white">
        <div className="alpha-container pt-10 md:pt-14 pb-2 md:pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[color:var(--alpha-line)]/70 border-y border-[color:var(--alpha-line)]/70">
            {STAT_STRIP.map(([k, v]) => (
              <div key={v} className="px-1 py-5 md:py-6 md:px-6 first:pl-0 last:pr-0">
                <div className="font-display text-[34px] md:text-[44px] font-medium leading-none tracking-tight text-[color:var(--alpha-purple-700)]">{k}</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)] font-semibold">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== TRUST MARQUEE ==================== */}
      <section className="bg-[color:var(--alpha-bg-soft)] border-b border-[color:var(--alpha-line)]">
        <div className="py-5 overflow-hidden alpha-marquee relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[color:var(--alpha-bg-soft)] to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[color:var(--alpha-bg-soft)] to-transparent z-10" />
          <div className="alpha-marquee-track flex gap-3 whitespace-nowrap w-max">
            {[...TRUST_PILLS, ...TRUST_PILLS].map((p, i) => (
              <span
                key={`${p}-${i}`}
                className="alpha-chip alpha-chip-neutral !py-2 !px-4 !text-[12px] shrink-0"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--alpha-purple-600)]" /> {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SERVICE TILES (VISUAL GRID) ==================== */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-6 md:mb-8 alpha-reveal">
            <div>
              <div className="alpha-overline">What we do</div>
              <h2 className="mt-2 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">
                One provider. Every support.
              </h2>
            </div>
            <Link to="/services" className="alpha-btn-ghost text-sm !py-2.5 !px-4" data-testid="home-all-services">
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {SERVICE_TILES.map((s) => {
              const Icon = s.Icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="relative group rounded-2xl overflow-hidden aspect-[4/5] md:aspect-[5/6] shadow-md hover:shadow-2xl transition-shadow"
                  data-testid={`home-service-tile-${s.slug}`}
                >
                  <img
                    src={s.img}
                    alt={s.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-[1200ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F1A5C]/95 via-[#2F1A5C]/45 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-white">
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center mb-2.5 group-hover:bg-[color:var(--alpha-green-500)] transition-colors">
                      <Icon className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                    <div className="font-display font-semibold text-[15px] md:text-[17px] leading-snug !text-white">
                      {s.label}
                    </div>
                    <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-white/85 group-hover:text-white group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== VACANCIES PREVIEW ==================== */}
      <section className="alpha-section-tight alpha-gradient-soft-band">
        <div className="alpha-container">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-6 md:mb-8 alpha-reveal">
            <div>
              <div className="alpha-overline">Our homes</div>
              <h2 className="mt-2 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">
                Real-estate-style, built for NDIS.
              </h2>
            </div>
            <Link to="/vacancies" className="alpha-btn-primary text-sm !py-2.5 !px-5" data-testid="home-vacancies-cta">
              View all vacancies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="grid md:grid-cols-3 gap-5">
              {[...Array(3)].map((_, i) => (
                <div key={`skeleton-${i}`} className="alpha-card overflow-hidden">
                  <div className="h-56 bg-[color:var(--alpha-bg-soft)] animate-pulse" />
                  <div className="p-5 space-y-2">
                    <div className="h-4 w-3/4 bg-[color:var(--alpha-bg-soft)] animate-pulse rounded" />
                    <div className="h-3 w-1/2 bg-[color:var(--alpha-bg-soft)] animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {featured.slice(0, 3).map((v) => <VacancyCard key={v.id} vacancy={v} />)}
            </div>
          )}
        </div>
      </section>

      {/* ==================== VIDEO STORIES ==================== */}
      <VideoStories
        eyebrow="Watch"
        title="See Alpha in action."
        subtitle="Three short explainers — our SIL homes, community participation, and the support workers behind every shift."
      />

      {/* ==================== DAY IN THE LIFE (editorial · human · warm) ==================== */}
      <DayInTheLife />

      {/* ==================== AUDIENCE CARDS (Participants · Families · Coordinators) ==================== */}
      <AudienceCards />

      {/* ==================== OUTCOMES BAND ==================== */}
      <section className="alpha-section-tight bg-white border-y border-[color:var(--alpha-line)]">
        <div className="alpha-container">
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {OUTCOMES.map((o, i) => (
              <div
                key={o.title}
                className="alpha-card p-6 flex items-start gap-4 hover:-translate-y-1"
                data-testid={`home-outcome-${i}`}
              >
                <div className="w-12 h-12 rounded-xl alpha-gradient text-white flex items-center justify-center shrink-0 shadow-md">
                  <o.Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-semibold text-lg leading-tight">{o.title}</div>
                  <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{o.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== REFERRAL CTA STRIP ==================== */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[24px] md:rounded-[32px] alpha-gradient p-8 md:p-12 text-white alpha-noise">
            <div className="alpha-orb alpha-orb-green w-[260px] h-[260px] right-[-40px] top-[-40px] opacity-60 hidden md:block" aria-hidden />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                  <ClipboardCheck className="w-3.5 h-3.5" /> Referral intake
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-[44px] font-medium leading-[1.05] !text-white">
                  Ready to start? <span className="alpha-serif-italic">We'll take it from here.</span>
                </h2>
                <p className="mt-3 text-white/80 max-w-xl text-[15px]">Participant, family or coordinator — one form, one business day response.</p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2" data-testid="cta-refer-strip">
                  Make a Referral <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "cta-strip")} className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> 1300 380 692
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURED HOME (real properties · cinematic) ==================== */}
      <FeaturedHome variant="home" testid="home-featured-home" />

      {/* ==================== FINAL CLOSER ==================== */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="grid lg:grid-cols-12 gap-8 items-center alpha-reveal">
            <div className="lg:col-span-6">
              <div className="alpha-overline">Locations</div>
              <h2 className="mt-2 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05]">
                Melbourne &amp; Perth hubs. National intake.
              </h2>
              <p className="mt-3 text-[color:var(--alpha-mute)] text-[15px] max-w-lg">
                Immediate service in VIC &amp; WA. NSW, QLD, SA, ACT, TAS &amp; NT supported through our national coordination model.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="alpha-card p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
                  <div>
                    <div className="font-display font-semibold leading-tight">Victoria hub</div>
                    <div className="text-xs text-[color:var(--alpha-mute)] mt-0.5">Docklands, Melbourne</div>
                  </div>
                </div>
                <div className="alpha-card p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[color:var(--alpha-green-100)] text-[color:var(--alpha-green-700)] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
                  <div>
                    <div className="font-display font-semibold leading-tight">Western Australia hub</div>
                    <div className="text-xs text-[color:var(--alpha-mute)] mt-0.5">West Perth, WA</div>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link to="/locations" className="alpha-btn-ghost text-sm !py-2.5 !px-4">See all locations</Link>
                <Link to="/referrals" className="alpha-btn-primary text-sm !py-2.5 !px-5">Start a referral <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
            <div className="lg:col-span-6 relative rounded-3xl overflow-hidden shadow-xl alpha-ring-gradient">
              <AnimatedAusMap className="w-full h-full block" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
